import React, { useState } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BASE_URL, POST_DEPARTMENT } from '../utlilities/config';
import { ToastContainer, toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiX, FiBriefcase } from 'react-icons/fi';

// Loading spinner component for consistency with your other modal
const Spinner = () => (
  <div className="flex justify-center items-center h-full w-full absolute inset-0 bg-white bg-opacity-80 z-[1100]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

// Reusable form field component matching your card modal
const FormField = ({ label, required, error, touched, children }) => (
  <div className="w-full">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {touched && error && (
      <p className="mt-1 text-sm text-red-500">{error}</p>
    )}
  </div>
);

const AddDoctorDepartment = ({ setDepartmentModal, refreshData, onDepartmentSaved }) => {
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('accessToken');

  const validationSchema = Yup.object({
    departmentName: Yup.string().required('Department name is required'),
    departmentID: Yup.string()
  });

  const formik = useFormik({
    initialValues: {
      departmentName: '',
      departmentID: '-1',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await axios.post(`${BASE_URL}${POST_DEPARTMENT}`, {}, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'departmentName': values.departmentName,
            'departmentID': values.departmentID,
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.status === 200) {
          toast.success('Department added successfully!', {
            autoClose: 1000,
          });
          const newDepartment = { DepartmentName: values.departmentName };
          onDepartmentSaved(newDepartment);
          refreshData();
          setDepartmentModal(false);
        }
      } catch (error) {
        console.error('Error submitting the form:', error);
        toast.error('Failed to add department. Please try again.', {
          autoClose: 1000,
        });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      
      {loading && <Spinner />}
      
      <div className="bg-white w-2/3 max-w-2xl rounded-2xl shadow-2xl transform transition-all duration-300 ease-out animate-fade-in overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-5 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <FiBriefcase className="mr-2" /> Add New Department
          </h2>
          <button 
            onClick={() => setDepartmentModal(false)}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors duration-200"
            aria-label="Close modal"
          >
            <FiX size={20} />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <FormField 
              label="Department Name" 
              required={true}
              error={formik.errors.departmentName}
              touched={formik.touched.departmentName}
            >
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                  <FiBriefcase />
                </div>
                <input
                  type="text"
                  name="departmentName"
                  placeholder="Enter Department Name"
                  className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200 ${
                    formik.touched.departmentName && formik.errors.departmentName
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-200 focus:border-blue-500 focus:ring-blue-200'
                  }`}
                  value={formik.values.departmentName}
                  onChange={(e) => {
                    const value = e.target.value || '';
                    formik.setFieldValue('departmentName', value.toUpperCase());
                  }}
                  onBlur={formik.handleBlur}
                />
              </div>
            </FormField>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => setDepartmentModal(false)}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-colors duration-200 flex items-center"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
            >
              Save Department
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDoctorDepartment;